/**
 * What the documents card says, separated from how it looks.
 *
 * A plain module rather than logic inside the component, for the same reason
 * `admin/access.ts` is one: there is no DOM in the test runner, so anything
 * worth a test has to be reachable without rendering. What is worth a test
 * here is the sentence under an empty list — three different reasons a folder
 * shows nothing, and the whole point of the server's `{connected, folder}`
 * shape is that the screen can tell them apart.
 */

export interface DriveFile {
  id: string
  name: string
  mimeType: string
  url: string | null
  modified: string | null
  size: number | null
  folder: boolean
}

export interface Documents {
  connected: boolean
  folder: { id: string; url: string | null; label: string | null } | null
  files: DriveFile[]
  /** The folders from the client's folder down to the one listed. Empty at the top. */
  trail?: { id: string; name: string }[]
}

/**
 * Why there is nothing to show, or null when there is.
 *
 * Ordered by what the reader can act on. "No Google account is connected" is a
 * deployment setting and there is nothing the person looking at the screen can
 * do about it, so it is said plainly and without a suggestion. "No folder is
 * linked" is one field on this client, so it says where to go. Only after both
 * of those is an empty list actually an empty folder.
 */
export function whyEmpty(d: Documents): string | null {
  if (!d.connected) {
    return 'No Google account is connected to this platform yet, so the folder cannot be read from here.'
  }
  if (d.folder === null) {
    return 'No Drive folder is linked to this client. Link one and everything in it shows up here.'
  }
  if (d.files.length === 0) return 'The folder is empty.'
  return null
}

const KINDS: [RegExp, string][] = [
  // Google's own, matched whole. Anchoring these matters: the first version
  // of this matched on the tail, `/\.document$/`, which also catches a .docx
  // — harmlessly, as it happens — while `/\.spreadsheet$/` misses an .xlsx,
  // which ends `spreadsheetml.sheet`. Half right by accident in both
  // directions is worse than either.
  [/^application\/vnd\.google-apps\.folder$/, 'Folder'],
  [/^application\/vnd\.google-apps\.document$/, 'Doc'],
  [/^application\/vnd\.google-apps\.spreadsheet$/, 'Sheet'],
  [/^application\/vnd\.google-apps\.presentation$/, 'Slides'],
  [/^application\/vnd\.google-apps\.form$/, 'Form'],

  [/^application\/pdf$/, 'PDF'],

  // What the rest of the world sends, which arrives in a client folder as
  // often as Google's own does — a brand book is a .docx from an agency.
  [/wordprocessingml|msword|opendocument\.text/, 'Doc'],
  [/spreadsheetml|ms-excel|opendocument\.spreadsheet/, 'Sheet'],
  [/presentationml|ms-powerpoint|opendocument\.presentation/, 'Slides'],

  [/^image\//, 'Image'],
  [/^video\//, 'Video'],
  [/^audio\//, 'Audio'],
  [/zip|compressed|x-tar|x-rar|x-7z/, 'Archive'],
]

/**
 * A word for a MIME type.
 *
 * An unknown type is "File" rather than the raw string, because
 * `application/vnd.openxmlformats-officedocument.wordprocessingml.document` in
 * a sixty-pixel column is not information. A raw type nobody recognises is
 * also how a Google-apps type nobody mapped would go unnoticed, so the list
 * above covers everything the studio actually puts in a folder.
 */
export function fileKind(mimeType: string): string {
  for (const [pattern, label] of KINDS) {
    if (pattern.test(mimeType)) return label
  }
  return 'File'
}

/**
 * Drive's timestamp as a day the platform's formatters understand.
 *
 * `modifiedTime` is a full RFC3339 instant. `formatDate` takes a date and
 * appends a midnight to it, so handing it the instant makes an invalid date
 * and it quietly renders the raw string — a column of
 * "2026-09-14T08:00:00.000Z" nobody would ever have called a bug report about,
 * because it looks deliberate.
 *
 * Converted through the local date rather than by cutting the first ten
 * characters off, because cutting keeps UTC: a file touched at nine in the
 * evening in Bengaluru would be dated to the day before, every time.
 */
export function driveDay(iso: string | null): string | null {
  if (iso === null) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

/**
 * A size somebody can read, or empty for the things that have none.
 *
 * Google's own formats report no size at all — a Doc does not have one in the
 * way a PDF does — and neither do folders. That is "nothing to say", not
 * "0 B", which would read as an empty file.
 */
export function readableSize(bytes: number | null): string {
  if (bytes === null || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`

  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  // One decimal below 10, none above: "1.4 MB" is useful, "247.3 MB" is not.
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${UNITS[unit]}`
}
